import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import GavelIcon from '@mui/icons-material/Gavel';
import API_CONFIG from '../../config/api';
import { apiFetch, getToken } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import { useAuth, getUserRole } from '../../contexts/AuthContext';
import { isFiscalSetorial, getFiscalSetor } from '../../utils/dataIsolation';
import {
  formatCurrency,
  parseValorMes,
  isPrestadorAtivo,
} from './useModuloFiscalData';
import './moduloFiscal.css';

function norm(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase();
}

async function openPdfComAuth(url, unidadeCodigo) {
  const token = getToken();
  try {
    const abs = new URL(url, window.location.origin);
    if (token) abs.searchParams.set('access_token', token);
    if (unidadeCodigo && !abs.searchParams.has('unidade')) {
      abs.searchParams.set('unidade', unidadeCodigo);
    }
    const openUrl = abs.toString();
    const win = window.open(openUrl, '_blank', 'noopener,noreferrer');
    if (win) return;
    const res = await fetch(openUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || 'Erro ao abrir PDF. Faça login novamente.');
      return;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
  } catch (e) {
    console.error('[openPdfComAuth]', e);
    alert('Erro ao abrir PDF.');
  }
}

function fmtVigencia(inicio, fim) {
  return `${inicio || '—'} → ${fim || '—'}`;
}

function termoDataMs(t) {
  const raw = t.data_assinatura || t.data_upload || t.criado_em || '';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function fmtDataTermo(t) {
  const ms = termoDataMs(t);
  if (!ms) return 'sem data';
  return new Date(ms).toLocaleDateString('pt-BR');
}

/**
 * Detalhe do contrato no módulo isolado do fiscal:
 * primitivo + aditivos, designação assinada (dropdown), PDFs e avaliação.
 */
export default function ModuloFiscalContratoDetalhePage() {
  const { fiscalId, contratoId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada, user, rbacRole, fiscalSetor, canAddAvaliacao } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fiscal, setFiscal] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [aditivos, setAditivos] = useState([]);
  const [termosAssinados, setTermosAssinados] = useState([]);
  const [desigOpen, setDesigOpen] = useState(false);
  const desigRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [resF, resP] = await Promise.all([
        apiFetch(withUnidade('/api/fiscais', unidadeSelecionada)),
        apiFetch(withUnidade('/api/prestadores', unidadeSelecionada)),
      ]);
      if (!resF.ok) throw new Error('Erro ao buscar fiscal');
      if (!resP.ok) throw new Error('Erro ao buscar contratos');

      const fiscais = await resF.json();
      const f = (Array.isArray(fiscais) ? fiscais : []).find(
        (x) => String(x.id) === String(fiscalId)
      );
      if (!f) throw new Error('Fiscal não encontrado nesta unidade.');

      const setorFiscalModulo = String(f.setor || '').trim();
      const role = rbacRole || getUserRole(user);
      if (isFiscalSetorial(user, role)) {
        const meuSetor = getFiscalSetor(user, fiscalSetor);
        if (meuSetor && norm(meuSetor) !== norm(setorFiscalModulo)) {
          throw new Error('Acesso restrito: você só pode ver contratos do seu setor.');
        }
      }
      setFiscal(f);

      const prestadores = await resP.json();
      const list = Array.isArray(prestadores) ? prestadores : [];
      const c = list.find(
        (p) =>
          String(p.id) === String(contratoId) ||
          String(p.numeroContrato) === String(contratoId)
      );
      if (!c || !isPrestadorAtivo(c)) {
        throw new Error('Contrato não encontrado nesta unidade.');
      }
      if (norm(c.setor) !== norm(setorFiscalModulo)) {
        throw new Error('Este contrato não pertence ao setor deste fiscal.');
      }
      setContrato(c);

      const resAd = await apiFetch(
        withUnidade(
          `/api/aditivos?numeroContrato=${encodeURIComponent(c.numeroContrato)}`,
          unidadeSelecionada
        )
      );
      let ads = [];
      if (resAd.ok) {
        const data = await resAd.json();
        ads = Array.isArray(data) ? data : [];
      }
      ads.sort((a, b) => {
        const na = parseInt(String(a.numeroAditivo || '0').replace(/\D/g, ''), 10) || 0;
        const nb = parseInt(String(b.numeroAditivo || '0').replace(/\D/g, ''), 10) || 0;
        return na - nb;
      });
      setAditivos(ads);

      try {
        const resT = await apiFetch(withUnidade('/api/termos-designacao', unidadeSelecionada));
        if (resT.ok) {
          const todos = await resT.json();
          const unidadeAtual = String(unidadeSelecionada?.codigo || '').trim().toUpperCase();
          const doContrato = (Array.isArray(todos) ? todos : [])
            .filter((t) => {
              if (String(t.numero_contrato) !== String(c.numeroContrato)) return false;
              if (Number(t.assinado) !== 1 || !(t.pdf_path || t.id)) return false;
              const uTermo = String(t.unidade || '').trim().toUpperCase();
              if (!uTermo) return false;
              if (unidadeAtual && uTermo !== unidadeAtual) return false;
              return true;
            })
            .sort((a, b) => termoDataMs(b) - termoDataMs(a));
          setTermosAssinados(doContrato);
        } else {
          setTermosAssinados([]);
        }
      } catch {
        setTermosAssinados([]);
      }
    } catch (e) {
      console.error('ModuloFiscalContratoDetalhe:', e);
      setError(e.message || 'Erro ao carregar contrato.');
      setContrato(null);
      setAditivos([]);
      setTermosAssinados([]);
    } finally {
      setLoading(false);
    }
  }, [fiscalId, contratoId, unidadeSelecionada, user, rbacRole, fiscalSetor]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!desigOpen) return undefined;
    const onDoc = (e) => {
      if (desigRef.current && !desigRef.current.contains(e.target)) setDesigOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [desigOpen]);

  const trilha = useMemo(() => {
    if (!contrato) return [];
    const nodes = [
      {
        key: 'primitivo',
        tipo: 'primitivo',
        titulo: 'Contrato primitivo',
        valorMes: contrato.valorMes,
        vigenciaInicio: contrato.vigenciaInicio,
        vigenciaFim: contrato.vigenciaFim,
        assinatura: contrato.assinatura,
        objeto: contrato.objeto,
        pdfOk: true,
        raw: contrato,
      },
    ];
    aditivos.forEach((ad) => {
      nodes.push({
        key: `ad-${ad.id}`,
        tipo: 'aditivo',
        titulo: `Aditivo ${ad.numeroAditivo || ad.id}`,
        valorMes: ad.valorMes,
        valorAnterior: ad.valor_anterior,
        vigenciaInicio: ad.vigenciaInicio,
        vigenciaFim: ad.vigenciaFim,
        assinatura: ad.assinatura,
        objeto: ad.objeto,
        pdfOk: !!ad.pdfPath,
        raw: ad,
      });
    });
    return nodes;
  }, [contrato, aditivos]);

  const termoAtual = termosAssinados[0] || null;
  const termosAnteriores = termosAssinados.slice(1);
  const podeAvaliar = contrato && canAddAvaliacao?.(contrato.setor);

  const abrirPdfPrimitivo = () => {
    if (!contrato?.id) return;
    openPdfComAuth(
      `${API_CONFIG.BACKEND_URL}/api/prestadores/${contrato.id}/contrato/pdf`,
      unidadeSelecionada?.codigo
    );
  };

  const abrirPdfAditivo = (ad) => {
    if (!ad?.id) return;
    openPdfComAuth(
      `${API_CONFIG.BACKEND_URL}/api/aditivos/${ad.id}/pdf`,
      unidadeSelecionada?.codigo
    );
  };

  const abrirPdfDesignacao = (termo) => {
    if (!termo?.id) return;
    openPdfComAuth(
      `${API_CONFIG.BACKEND_URL}/api/termos-designacao/${termo.id}/pdf`,
      unidadeSelecionada?.codigo
    );
  };

  const irAvaliacao = () => {
    if (!contrato) return;
    navigate('/avaliacao-fornecedores', {
      state: {
        openAvaliacaoNumeroContrato: contrato.numeroContrato,
        returnTo: `/modulos/fiscal/${fiscalId}/contratos/${contrato.id}`,
      },
    });
  };

  if (loading) {
    return (
      <div className="mf-page mf-page--embedded">
        <div className="mf-state">
          <CircularProgress sx={{ color: 'var(--ep-accent)' }} />
          <p>Carregando contrato…</p>
        </div>
      </div>
    );
  }

  if (error || !contrato) {
    return (
      <div className="mf-page mf-page--embedded">
        <div className="mf-state">
          <p>{error || 'Contrato não encontrado.'}</p>
          <button
            type="button"
            className="mf-page__back"
            onClick={() => navigate(`/modulos/fiscal/${fiscalId}/contratos`)}
          >
            Voltar à lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mf-page mf-page--embedded">
      <header className="mf-page__header">
        <div>
          <button
            type="button"
            className="mf-page__back mf-page__back--inline"
            onClick={() => navigate(`/modulos/fiscal/${fiscalId}/contratos`)}
          >
            <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Lista de contratos
          </button>
          <h1 className="mf-page__title">{contrato.empresa}</h1>
          <p className="mf-page__meta">
            Nº <strong>{contrato.numeroContrato}</strong>
            {fiscal?.setor ? ` · Setor: ${fiscal.setor}` : ''}
            {unidadeSelecionada?.nome ? ` · ${unidadeSelecionada.nome}` : ''}
          </p>
        </div>
        <div className="mf-page__actions">
          <button type="button" className="mf-btn mf-btn--ghost" onClick={abrirPdfPrimitivo}>
            <PictureAsPdfIcon sx={{ fontSize: 18 }} />
            PDF contrato
          </button>

          {termoAtual ? (
            <div className={`mf-desig${desigOpen ? ' mf-desig--open' : ''}`} ref={desigRef}>
              <div className="mf-desig__main">
                <button
                  type="button"
                  className="mf-btn mf-btn--ghost mf-desig__latest"
                  onClick={() => abrirPdfDesignacao(termoAtual)}
                  title={`Designação assinada em ${fmtDataTermo(termoAtual)}`}
                >
                  <GavelIcon sx={{ fontSize: 18 }} />
                  Designação ({fmtDataTermo(termoAtual)})
                </button>
                {termosAnteriores.length > 0 && (
                  <button
                    type="button"
                    className="mf-desig__caret"
                    aria-expanded={desigOpen}
                    aria-label="Ver designações anteriores"
                    onClick={() => setDesigOpen((v) => !v)}
                  >
                    <ArrowDropDownIcon />
                  </button>
                )}
              </div>
              {desigOpen && termosAnteriores.length > 0 && (
                <ul className="mf-desig__menu" role="menu">
                  {termosAnteriores.map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          abrirPdfDesignacao(t);
                          setDesigOpen(false);
                        }}
                      >
                        <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                        Designação {fmtDataTermo(t)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <span
              className="mf-page__meta"
              title="Somente termos assinados (Prestadores → Adicionar termos)"
            >
              Sem designação assinada
            </span>
          )}

          {podeAvaliar ? (
            <button type="button" className="mf-btn mf-btn--primary" onClick={irAvaliacao}>
              <RateReviewIcon sx={{ fontSize: 18 }} />
              Avaliar fornecedor
            </button>
          ) : (
            <span className="mf-page__meta">Avaliação indisponível para seu perfil/setor</span>
          )}
        </div>
      </header>

      <p className="mf-note">
        Trilha do contrato. Designação: apenas documentos assinados (Prestadores → Adicionar termos),
        acumulativos no dropdown. Isolamento por unidade
        {fiscal?.setor ? ` e setor (${fiscal.setor})` : ''}.
      </p>

      <div className="mf-resumo">
        <div className="mf-kpi">
          <span className="mf-kpi__label">Valor vigente (mês)</span>
          <span className="mf-kpi__value">
            {formatCurrency(
              parseValorMes(
                aditivos.length
                  ? aditivos[aditivos.length - 1].valorMes ?? contrato.valorMes
                  : contrato.valorMes
              )
            )}
          </span>
        </div>
        <div className="mf-kpi">
          <span className="mf-kpi__label">Aditivos</span>
          <span className="mf-kpi__value">{aditivos.length}</span>
        </div>
        <div className="mf-kpi">
          <span className="mf-kpi__label">Vigência atual</span>
          <span className="mf-kpi__value mf-kpi__value--sm">
            {fmtVigencia(
              aditivos.length
                ? aditivos[aditivos.length - 1].vigenciaInicio || contrato.vigenciaInicio
                : contrato.vigenciaInicio,
              aditivos.length
                ? aditivos[aditivos.length - 1].vigenciaFim || contrato.vigenciaFim
                : contrato.vigenciaFim
            )}
          </span>
        </div>
      </div>

      <h2 className="mf-section-title">Trilha do contrato</h2>
      <ol className="mf-trilha">
        {trilha.map((node, idx) => (
          <li key={node.key} className={`mf-trilha__item mf-trilha__item--${node.tipo}`}>
            <div className="mf-trilha__marker" aria-hidden="true">
              <span>{idx + 1}</span>
            </div>
            <div className="mf-trilha__card">
              <div className="mf-trilha__card-top">
                <span className={`mf-chip mf-chip--${node.tipo}`}>{node.titulo}</span>
                {node.tipo === 'aditivo' && node.pdfOk && (
                  <button
                    type="button"
                    className="mf-btn mf-btn--ghost mf-btn--sm"
                    onClick={() => abrirPdfAditivo(node.raw)}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                    PDF
                  </button>
                )}
                {node.tipo === 'primitivo' && (
                  <button
                    type="button"
                    className="mf-btn mf-btn--ghost mf-btn--sm"
                    onClick={abrirPdfPrimitivo}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                    PDF
                  </button>
                )}
              </div>
              <div className="mf-trilha__grid">
                <div>
                  <span className="mf-trilha__label">Valor mensal</span>
                  <strong>{formatCurrency(parseValorMes(node.valorMes))}</strong>
                </div>
                {node.valorAnterior != null && node.valorAnterior !== '' && (
                  <div>
                    <span className="mf-trilha__label">Valor anterior</span>
                    <strong>{formatCurrency(parseValorMes(node.valorAnterior))}</strong>
                  </div>
                )}
                <div>
                  <span className="mf-trilha__label">Vigência</span>
                  <strong>{fmtVigencia(node.vigenciaInicio, node.vigenciaFim)}</strong>
                </div>
                {node.assinatura && (
                  <div>
                    <span className="mf-trilha__label">Assinatura</span>
                    <strong>{node.assinatura}</strong>
                  </div>
                )}
              </div>
              {node.objeto && <p className="mf-trilha__objeto">{node.objeto}</p>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
